import {
  ChangeDetectorRef, Component, ElementRef,
  EventEmitter, OnChanges, OnDestroy, OnInit, AfterViewInit, SimpleChanges,
  Input,  Output, ViewChild,
} from '@angular/core';
import { BusyService } from '@unleashed/services/common';
import { User } from '@unleashed/models/account';

// Apple Pay is only available on Safari/iOS - loading the module on other browsers causes uncaught errors.
// The Stencil lazy-loader in webcomponents.esm.js only initializes Apple Pay when the element appears
// in the DOM (guarded by *ngIf="showApplePay"), so a dynamic import isn't strictly required here
// unlike the command-center React pattern where module-level imports trigger registration immediately.
const isApplePayCapable = 'ApplePaySession' in window;

@Component({
  selector: 'ua-justifi',
  templateUrl: './justifi.component.html',
  styleUrls: ['./justifi.component.scss']
})
export class JustifiWrapperComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('justifiElem') justifiElem?: ElementRef;
  @Input() authToken: string | undefined;
  @Input() checkoutId: string | undefined;
  @Input() isAvs = false;
  @Input() isCanada = false;
  @Input() isAgreementChecked = false;
  @Input() merchantName?: string;
  @Input() user?: User;
  @Output() justifiReady = new EventEmitter<ElementRef>();

  showApplePay = false;
  googlePayVisible = false;

  private blockEnterHandler?: (e: KeyboardEvent) => void;
  private submitEventHandler?: () => void;
  private walletCancelledHandler?: () => void;
  private googlePayObserver?: ResizeObserver;
  private googlePayTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private busyService: BusyService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.busyService.set('checkout', true);
    this.showApplePay = isApplePayCapable;
  }

  ngAfterViewInit(): void {
    const checkout = this.justifiElem?.nativeElement;
    if (!checkout) {
      return;
    }

    // Prevent Enter key from triggering JustiFi's internal shadow DOM form submission.
    // The card inputs live inside a shadow DOM <form>. Enter in those inputs causes a native
    // form submission (page reload) that our outer form can't intercept. Using capture phase
    // ensures we block it before it reaches the shadow DOM.
    this.blockEnterHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault();
      }
    };
    checkout.addEventListener('keydown', this.blockEnterHandler, true);

    // Show spinner when Apple Pay / Google Pay initiates payment
    this.submitEventHandler = () => {
      this.busyService.set('pay', true);
      this.cdr.detectChanges();
    };
    checkout.addEventListener('submit-event', this.submitEventHandler);

    // When Google Pay or Apple Pay is canceled, the modular checkout sets selectedPaymentMethod
    // to undefined. Reset it back to new_card so the card form can be used for manual entry.
    this.walletCancelledHandler = () => {
      checkout.setSelectedPaymentMethod?.({ type: 'new_card' });
      this.busyService.set('pay', false);
      this.cdr.detectChanges();
    };
    checkout.addEventListener('googlePayCancelled', this.walletCancelledHandler);
    checkout.addEventListener('applePayCancelled', this.walletCancelledHandler);

    // Detect Google Pay visibility via ResizeObserver instead of a fixed timeout.
    // The justifi-google-pay element renders with offsetHeight 0 until JustiFi's internal
    // isReadyToPay resolves. ResizeObserver fires as soon as the element gains height,
    // avoiding the race condition where a hard-coded delay is too short on slower devices.
    const googlePay = checkout.querySelector('justifi-google-pay');
    if (googlePay) {
      this.googlePayObserver = new ResizeObserver(() => {
        if ((googlePay as HTMLElement).offsetHeight > 0) {
          this.googlePayVisible = true;
          this.googlePayObserver?.disconnect();
          this.googlePayObserver = undefined;
          // Re-assert selectedPaymentMethod after the Google Pay wrapper DOM transition to prevent
          // the modular checkout's queryFormRefs from resetting it when the wrapper changes position.
          checkout.setSelectedPaymentMethod?.({ type: 'new_card' });
          this.cdr.detectChanges();
        }
      });
      this.googlePayObserver.observe(googlePay);

      // Safety net: stop observing after 10s if Google Pay never renders (not available on device)
      this.googlePayTimeout = setTimeout(() => {
        this.googlePayObserver?.disconnect();
        this.googlePayObserver = undefined;
      }, 10000);
    }

    setTimeout(() => {
      // Re-apply disabled state after web components initialize — static disabled="true" in the
      // template may be ignored by components that register asynchronously via the Stencil loader.
      this.updateApplePayDisabled();

      checkout.setSelectedPaymentMethod?.({ type: 'new_card' });

      this.justifiReady.emit(this.justifiElem);
      this.busyService.set('checkout', false);
      this.cdr.detectChanges();
    }, 3500); // need a few sec for components to initialize
  }

  ngOnDestroy(): void {
    const checkout = this.justifiElem?.nativeElement;
    if (!checkout) {
      return;
    }

    if (this.blockEnterHandler) {
      checkout.removeEventListener('keydown', this.blockEnterHandler, true);
    }

    if (this.submitEventHandler) {
      checkout.removeEventListener('submit-event', this.submitEventHandler);
    }

    if (this.walletCancelledHandler) {
      checkout.removeEventListener('googlePayCancelled', this.walletCancelledHandler);
      checkout.removeEventListener('applePayCancelled', this.walletCancelledHandler);
    }

    this.googlePayObserver?.disconnect();
    if (this.googlePayTimeout) {
      clearTimeout(this.googlePayTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isAgreementChecked) {
      this.updateApplePayDisabled();
      // Google Pay agreement gating is handled via pointer-events/opacity in the template.
      // No disabled attribute toggle needed — it was unreliable with the iframe-based component.
    }
  }

  async useAccountAddress(): Promise<void> {
    if (!this.justifiElem?.nativeElement || !this.user) {
      return;
    }

    await this.justifiElem.nativeElement.fillBillingForm({
      name: this.user.firstName && this.user.lastName ? `${this.user.firstName} ${this.user.lastName}` : '',
      address_line1: this.user.address?.streetAddress ?? '',
      address_city: this.user.address?.city ?? '',
      address_state: this.user.address?.state ?? '',
      address_postal_code: this.user.address?.zipCode ?? '',
    });
  }

  private updateApplePayDisabled(): void {
    const applePay = this.justifiElem?.nativeElement?.querySelector('justifi-apple-pay');
    if (!applePay) {
      return;
    }

    if (this.isAgreementChecked) {
      applePay.removeAttribute('disabled');
    }
    else {
      applePay.setAttribute('disabled', 'true');
    }
  }

}
